import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { getCurrentUser, getProfile, getBirthData } from '../lib/auth';
import './Profile.css';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form data
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthTimezone, setBirthTimezone] = useState('Europe/Helsinki');
  const [birthPlaceName, setBirthPlaceName] = useState('');
  const [birthLat, setBirthLat] = useState('');
  const [birthLng, setBirthLng] = useState('');
  const [birthTimeAccuracy, setBirthTimeAccuracy] = useState('exact');

  useEffect(() => {
    const fetchData = async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }

      try {
        // Hae käyttäjä
        const { user: currentUser, error: userError } = await getCurrentUser();
        if (userError || !currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Hae profiili
        const { data: profile, error: profileError } = await getProfile(currentUser.id);
        if (profileError && profileError.code !== 'PGRST116') {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Profile fetch error:', profileError);
          }
        }

        if (profile) {
          setFullName(profile.full_name || '');
        }

        // Hae syntymätiedot
        const { data: birthData, error: birthError } = await getBirthData(currentUser.id);
        if (birthError && birthError.code !== 'PGRST116') {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Birth data fetch error:', birthError);
          }
        }

        if (birthData) {
          // Lue date ja time erillisinä kenttinä
          setBirthDate(birthData.date || '');
          setBirthTime(birthData.time || '');
          setBirthTimezone(birthData.timezone || 'Europe/Helsinki');
          setBirthPlaceName(birthData.location || ''); // place_name -> location
          setBirthLat(birthData.lat ? String(birthData.lat) : ''); // birth_lat -> lat
          setBirthLng(birthData.lng ? String(birthData.lng) : ''); // birth_lng -> lng
          setBirthTimeAccuracy(birthData.birth_time_accuracy || 'exact');
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Profile: Error:', err);
        }
        setError('Tietojen haku epäonnistui.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);

    if (!supabase || !user) {
      setError('Et ole kirjautunut.');
      setSaving(false);
      return;
    }

    if (!fullName) {
      setError('Nimi on pakollinen.');
      setSaving(false);
      return;
    }

    // Syntymätiedot eivät ole pakollisia profiilissa (ne täytetään profile-setup:ssa)
    // Mutta jos täytetään, ne pitää täyttää oikein
    if (birthDate || birthTime || birthPlaceName) {
      if (!birthDate || !birthTime || !birthPlaceName) {
        setError('Jos täytät syntymätiedot, täytä kaikki kentät (päivä, aika, paikka).');
        setSaving(false);
        return;
      }
    }

    try {
      // Varmista että käyttäjä on oikea
      const { user: authUser, error: authError } = await getCurrentUser();
      if (authError || !authUser || authUser.id !== user.id) {
        throw new Error('Autentikointi epäonnistui');
      }

      // Upsert profiili
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: authUser.id,
            user_id: authUser.id,
            full_name: fullName,
          },
          { onConflict: 'id' }
        );

      if (profileError) {
        throw profileError;
      }

      // Upsert syntymätiedot (jos täytetty)
      if (birthDate && birthPlaceName) {
        // Runtime assert: birthDate on pakollinen
        if (!birthDate) {
          throw new Error('birthDate is required but was missing');
        }

        // Varmista että date on ISO-muodossa YYYY-MM-DD
        const isoDate = birthDate; // type="date" antaa automaattisesti YYYY-MM-DD
        
        const payload = {
          user_id: authUser.id,
          date: isoDate, // YYYY-MM-DD (NOT NULL)
          time: birthTime || '12:00', // aina arvo, ei null
          location: birthPlaceName, // pakollinen
          timezone: birthTimezone || 'Europe/Helsinki',
          lat: birthLat ? parseFloat(birthLat) : null,
          lng: birthLng ? parseFloat(birthLng) : null,
          birth_time_accuracy: birthTimeAccuracy,
        };

        // Loggaa payload vain dev-ympäristössä
        if (process.env.NODE_ENV !== 'production') {
          console.log('BIRTH PAYLOAD', payload);
        }

        const { error: birthError } = await supabase
          .from('birth_data')
          .upsert(payload, { onConflict: 'user_id' });

        if (birthError) {
          console.error('Birth data upsert error:', birthError);
          if (process.env.NODE_ENV !== 'production') {
            console.error('Birth data payload:', payload);
          }
          throw birthError;
        }
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Profile: Save error:', err);
      } else {
        console.error('Profile: Save error:', err.message || err);
      }
      if (err.message?.includes('RLS') || err.code === '42501') {
        setError('Kirjautuminen vaaditaan. Kirjaudu sisään ja yritä uudelleen.');
      } else {
        setError(`Tallennus epäonnistui: ${err.message || 'Tuntematon virhe'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="loading" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="moon-loader">
              <div className="moon"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="profile-page">
      <div className="container">
        <h1>Profiili</h1>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">Profiili tallennettu onnistuneesti!</div>}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="fullName">Nimi *</label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Koko nimesi"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthDate">Syntymäpäivä</label>
            <input
              id="birthDate"
              name="birthDate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthTime">Syntymäaika</label>
            <input
              id="birthTime"
              name="birthTime"
              type="time"
              value={birthTime}
              onChange={(e) => setBirthTime(e.target.value)}
            />
            {birthTime && birthTimeAccuracy !== 'exact' && (
              <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'rgba(255, 193, 7, 0.9)' }}>
                Syntymäaika: {birthTime} (arvioitu)
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="birthTimezone">Aikavyöhyke</label>
            <select
              id="birthTimezone"
              name="birthTimezone"
              value={birthTimezone}
              onChange={(e) => setBirthTimezone(e.target.value)}
            >
              <option value="Europe/Helsinki">Europe/Helsinki</option>
              <option value="Europe/Stockholm">Europe/Stockholm</option>
              <option value="Europe/Oslo">Europe/Oslo</option>
              <option value="Europe/Copenhagen">Europe/Copenhagen</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="birthPlaceName">Syntymäpaikka</label>
            <input
              id="birthPlaceName"
              name="birthPlaceName"
              type="text"
              value={birthPlaceName}
              onChange={(e) => setBirthPlaceName(e.target.value)}
              placeholder="esim. Helsinki, Finland"
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthLat">Leveysaste (valinnainen)</label>
            <input
              id="birthLat"
              name="birthLat"
              type="number"
              step="any"
              value={birthLat}
              onChange={(e) => setBirthLat(e.target.value)}
              placeholder="esim. 60.1699"
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthLng">Pituusaste (valinnainen)</label>
            <input
              id="birthLng"
              name="birthLng"
              type="number"
              step="any"
              value={birthLng}
              onChange={(e) => setBirthLng(e.target.value)}
              placeholder="esim. 24.9384"
            />
          </div>

          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Tallennetaan...' : 'Tallenna profiili'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
